import { Check, X } from 'lucide-react-native';
import { StyleSheet, View, type ViewStyle } from 'react-native';

import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';

/** How a row is drawn. Every value comes from the server's step_states (see SocialStepper). */
export type Look = 'done' | 'active' | 'pending' | 'locked' | 'failed';

export type SocialStepBulletProps = {
  number: number;
  look: Look;
  isLast: boolean;
  testID: string;
};

/** A step's ring (number, check or cross) and the line down to the next one. */
export function SocialStepBullet({ number, look, isLast, testID }: SocialStepBulletProps) {
  const numbered = look === 'active' || look === 'pending' || look === 'locked';
  return (
    <View style={styles.rail}>
      <View
        style={[styles.bullet, BULLET[look]]}
        accessible
        accessibilityLabel={`Step ${number}, ${LOOK_LABEL[look]}`}
        testID={`${testID}-bullet-${look}`}
      >
        {look === 'done' ? <Check size={16} color={colors.frost} strokeWidth={3} /> : null}
        {look === 'failed' ? <X size={16} color={colors.frost} strokeWidth={3} /> : null}
        {numbered ? (
          <Text variant="captionBold" color={look === 'locked' ? 'textMuted' : 'deepGreen'}>
            {number}
          </Text>
        ) : null}
      </View>
      {!isLast ? <View style={[styles.connector, look === 'done' && styles.connectorDone]} /> : null}
    </View>
  );
}

const BULLET_SIZE = 28;

/** What VoiceOver says for the ring (a check or a number alone doesn't say which). */
const LOOK_LABEL: Record<Look, string> = {
  done: 'done',
  active: 'your next step',
  pending: 'in progress',
  locked: 'not started',
  failed: 'did not go through',
};

const BULLET: Record<Look, ViewStyle> = {
  done: { backgroundColor: colors.electricGreen },
  active: { backgroundColor: colors.brightGreenSoft, borderWidth: 2, borderColor: colors.electricGreen },
  pending: { backgroundColor: colors.brightGreenSoft, borderWidth: 2, borderColor: colors.electricGreen },
  locked: { backgroundColor: colors.surface, borderWidth: 1.5, borderColor: colors.border },
  failed: { backgroundColor: colors.danger },
};

const styles = StyleSheet.create({
  rail: {
    width: BULLET_SIZE,
    alignItems: 'center',
  },
  bullet: {
    width: BULLET_SIZE,
    height: BULLET_SIZE,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Figma: a 4pt line from just under the ring down to the next one (steps sit 8pt apart).
  connector: {
    flex: 1,
    minHeight: spacing.xs,
    width: 2,
    marginTop: 2,
    backgroundColor: colors.border,
  },
  connectorDone: {
    backgroundColor: colors.electricGreen,
  },
});
